import React from 'react';
import {
  Alert,
  BusinessIcon,
  Select,
  SelectItem,
  SelectTrigger,
  Skeleton,
  Text,
} from '@billda/design-system';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../hooks/useOrganization';
import { useAuthStore } from '../../auth/stores/authStore';

const OrganizationDisplay: React.FC = () => {
  const { data, isLoading: loading, error } = useOrganization();
  const { isAuthenticated } = useAuthStore();
  const navigate = useNavigate();

  const currentOrganization = data?.currentOrganisation || null;
  const availableOrganizations = data?.availableOrganisations || [];

  const handleOrganizationChange = (organizationId: string) => {
    const selectedOrg = availableOrganizations.find(
      org => org.id === organizationId
    );
    if (selectedOrg) {
      navigate(
        `/organization/switching/${selectedOrg.id}/${encodeURIComponent(selectedOrg.name)}`
      );
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  if (loading) {
    return (
      <div
        data-testid="organization-loading"
        className="flex items-center gap-2 border-y border-border-default px-4 py-2"
      >
        <BusinessIcon
          aria-hidden="true"
          className="size-5 text-content-secondary"
        />

        <div className="flex-1">
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={16} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="organization-error"
        className="border-y border-border-default px-4 py-2"
      >
        <Alert
          data-testid="organization-error-alert"
          severity="warning"
          className="px-2 py-1 text-[0.75rem]"
        >
          {/* Use error.message to match E2E test expectations */}
          {error instanceof Error
            ? error.message
            : 'Failed to load organization'}
        </Alert>
      </div>
    );
  }

  if (!currentOrganization) {
    // Show loading state when we don't have organization data but are authenticated
    return (
      <div
        data-testid="organization-loading-no-data"
        className="flex items-center gap-2 border-y border-border-default px-4 py-2"
      >
        <BusinessIcon
          aria-hidden="true"
          className="size-5 text-content-secondary"
        />

        <div className="flex-1">
          <Skeleton variant="text" width="80%" height={20} />
          <Skeleton variant="text" width="60%" height={16} />
        </div>
      </div>
    );
  }

  const hasAvailableOrganizations =
    availableOrganizations && availableOrganizations.length > 0;

  return (
    <div
      data-testid="organization-display"
      className="flex items-center gap-2 border-y border-border-default px-4 py-2"
    >
      <BusinessIcon aria-hidden="true" className="size-5 text-content-accent" />

      <div className="min-w-0 flex-1">
        <Text
          as="div"
          data-testid="organization-label"
          variant="caption"
          color="secondary"
          className="mb-1 block leading-tight"
        >
          Organisation
        </Text>
        {hasAvailableOrganizations ? (
          <Select
            value={currentOrganization.id}
            onValueChange={handleOrganizationChange}
          >
            <SelectTrigger
              data-testid="organisation-selector"
              className="h-9 w-full bg-surface-default px-3 type-body-md font-medium text-content-primary"
            />
            {availableOrganizations.map(org => (
              <SelectItem key={org.id} value={org.id}>
                {org.name}
              </SelectItem>
            ))}
          </Select>
        ) : (
          <Text
            as="div"
            variant="body2"
            noWrap
            className="font-medium text-content-primary"
          >
            {currentOrganization.name}
          </Text>
        )}
      </div>
    </div>
  );
};

export default OrganizationDisplay;
